import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useItemDialogFocus from "../useItemDialogFocus";
import { getDocumentUnitName } from "../../utils/unitNames";
import { itemSupplierMatches } from "../../utils/supplierIdentity";
import { getPurchaseOrderEditingTranslator } from "./purchaseOrderEditingMessages";
import usePurchasePricePreviews, { PURCHASE_PRICE_PAGE_SIZE } from "../../hooks/usePurchasePricePreviews";
import PurchasePricePreview, { PurchasePricePagination, purchasePriceText } from "../PurchasePricePreview";
import "./ItemClassificationPicker.css";

const normalizeSearch = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase().trim();
const positiveId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
const supplierRelations = (item, supplierId) => (item.item_suppliers || [])
  .filter((relation) => itemSupplierMatches(relation, supplierId));
const supplierCodes = (item, supplierId) => [...new Set([
  item.supplier_code, ...supplierRelations(item, supplierId).map((relation) => relation.supplier_code),
].map((code) => String(code || "").trim()).filter(Boolean))];

/** Only receives the caller's authorized catalogue; selecting never writes a document. */
export default function PurchaseOrderItemPicker({ isOpen, items = [], lines = [], supplierId, businessUnitId, date, currency = "EUR", units = [], onSelect, onClose }) {
  const { themeId, languageId } = usePmsTheme();
  const t = getPurchaseOrderEditingTranslator(languageId);
  const id = useId();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const searchRef = useRef(null);
  const selectedRef = useRef(false);
  const dialogRef = useItemDialogFocus(isOpen, false, onClose);
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    selectedRef.current = false;
    searchRef.current?.focus();
  }, [isOpen, supplierId]);

  const existingIds = new Set(lines.map((line) => positiveId(line.item_id || line.item?.id)).filter(Boolean));
  const seen = new Set();
  const availableItems = positiveId(supplierId) ? items.filter((item) => {
    const itemId = positiveId(item?.id);
    if (!itemId || existingIds.has(itemId) || seen.has(itemId) || !supplierRelations(item, supplierId).length) return false;
    seen.add(itemId);
    return true;
  }) : [];
  const terms = normalizeSearch(query).split(/\s+/).filter(Boolean);
  const filteredItems = availableItems.filter((item) => {
    const text = normalizeSearch([item.name, item.code, ...supplierCodes(item, supplierId)].join(" "));
    return terms.every((term) => text.includes(term));
  });
  useEffect(() => setPage(1), [query, supplierId, businessUnitId, date, currency, isOpen]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PURCHASE_PRICE_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleItems = filteredItems.slice((currentPage - 1) * PURCHASE_PRICE_PAGE_SIZE, currentPage * PURCHASE_PRICE_PAGE_SIZE);
  const pricePreviews = usePurchasePricePreviews({
    items: visibleItems, businessUnitId, supplierId, date, currency, enabled: isOpen && Boolean(supplierId),
  });
  const packagingLabel = (item) => {
    const unit = getDocumentUnitName(item.unit, item.unit_id, units, languageId);
    const subunit = getDocumentUnitName(item.subunit, item.subunit_id, units, languageId);
    const pack = Number(item.pack_size || 1);
    if (!unit && !subunit) return t("No packaging configured");
    const formattedPack = new Intl.NumberFormat(languageId || "en", { maximumFractionDigits: 8, useGrouping: false }).format(pack);
    return pack > 1 ? `${unit || subunit} (${formattedPack} × ${subunit || unit})` : unit || subunit;
  };
  const choose = (item) => {
    if (selectedRef.current || !availableItems.some((entry) => positiveId(entry.id) === positiveId(item.id))) return;
    selectedRef.current = true;
    onSelect(item);
    onClose();
  };

  if (!isOpen) return null;
  return createPortal(
    <div className="pilcore-purchasing-ui icp-overlay" data-theme={themeId} onClick={(event) => {
      event.stopPropagation();
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="icp-dialog" ref={dialogRef} role="dialog" aria-modal="true"
        aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} tabIndex={-1}>
        <header>
          <div>
            <h3 id={`${id}-title`}>{t("Add Item")}</h3>
            <p id={`${id}-description`}>{t("Choose an item from this supplier to add it to the order.")}</p>
          </div>
          <button type="button" className="icp-button" onClick={onClose}>{t("Close")}</button>
        </header>
        <div className="icp-content">
          <label htmlFor={`${id}-search`}>
            {t("Search by item name, code or supplier code")}
            <input id={`${id}-search`} ref={searchRef} type="search" value={query}
              onChange={(event) => setQuery(event.target.value)} />
          </label>
          <p role="status">{t(filteredItems.length === 1 ? "{0} item available" : "{0} items available", { 0: filteredItems.length })}</p>
          <div className="icp-cards">
            {visibleItems.map((item) => <button key={item.id} type="button" className="icp-card" onClick={() => choose(item)}>
              <strong>{[item.code, item.name || t("Item #{0}", { 0: item.id })].filter(Boolean).join(" · ")}</strong>
              <small>{t("Packaging: {0}", { 0: packagingLabel(item) })}</small>
              {supplierCodes(item, supplierId).length > 0 && <small>{t("Supplier code: {0}", { 0: supplierCodes(item, supplierId).join(" · ") })}</small>}
              <PurchasePricePreview price={pricePreviews.prices[item.id]} loading={pricePreviews.loading}
                error={pricePreviews.error} item={item} units={units} languageId={languageId} />
            </button>)}
          </div>
          <PurchasePricePagination page={currentPage} pageCount={pageCount} onPageChange={setPage}
            languageId={languageId} total={filteredItems.length} />
          {pricePreviews.error && <button type="button" className="icp-button" onClick={pricePreviews.retry}>
            {purchasePriceText(languageId).retry}
          </button>}
          {!filteredItems.length && <p>{t(!positiveId(supplierId)
            ? "Choose a supplier before adding items."
            : !availableItems.length ? "No more items are available for this supplier." : "No items match your search.")}</p>}
          <p>{t("Items already in the order are not listed.")}</p>
        </div>
      </section>
    </div>, document.body
  );
}
